import m, {type Children, type ComponentType, MithrilComponent} from '@bitstillery/mithril'
import {api, logger} from '@bitstillery/common/app'
import {cached} from '@bitstillery/common/lib/api_cache'

import {import_compiled_cms_module, resolve_cms_component} from '@/cms/runtime/compile'

interface FragmentSource {
    name: string
    compiled_js: string
}

interface CmsFragmentCacheEntry {
    component: ComponentType
    compiled_js: string
}

/**
 * Name → compiled component registry, the fragment analogue of `cms_page_cache`. Populated once by
 * `load_cms_fragments()` during `App.init` (server and client) so layout chrome that references a
 * fragment — the footer, the breadcrumb menu — renders synchronously on the first paint, SSR included.
 * Global so the portal page and the preview frame share it, and reused across SSR requests in a process.
 */
export const cms_fragment_cache = new Map<string, CmsFragmentCacheEntry>()

/**
 * Published fragments for the current account as `{name, compiled_js}` strings. Wrapped in `cached()`
 * (like `AsyncData`) so the SSR process fetches+memoizes once and hands the strings to the client,
 * which recompiles them on its first frame — exactly how `$s.page.compiled_js` crosses the boundary.
 * Account-scoped: a per-account fragment overrides the global one server-side (see the public endpoint).
 */
const load_published_fragments = cached(
    'cms_fragments',
    async (): Promise<FragmentSource[]> => {
        const {result, success} = await api.get('portal/cms/fragments')
        return success ? result : []
    },
    {ttl: 60_000, scope: 'account', handoff: true, store_if: (v) => Array.isArray(v)},
)

/**
 * Fetch (or peek the SSR handoff of) the published fragments and compile each into
 * `cms_fragment_cache`. Idempotent and resilient: a single fragment that fails to compile is skipped
 * (logged) so it can't take down the chrome. Awaited in `App.init` before the first render.
 */
export async function load_cms_fragments(): Promise<void> {
    let sources: FragmentSource[]
    try {
        sources = await load_published_fragments()
    } catch (e) {
        logger.warn(`load_cms_fragments failed: ${e instanceof Error ? e.message : String(e)}`)
        return
    }

    await Promise.all(
        sources.map(async ({name, compiled_js}) => {
            try {
                const mod = await import_compiled_cms_module(compiled_js)
                const component = resolve_cms_component(mod)
                if (component) {
                    cms_fragment_cache.set(name, {component, compiled_js})
                } else {
                    logger.warn(`cms fragment '${name}' has no usable export`)
                }
            } catch (e) {
                logger.warn(`cms fragment '${name}' failed to compile: ${e instanceof Error ? e.message : String(e)}`)
            }
        }),
    )
}

export interface CmsFragmentAttrs {
    /** The published fragment's name (e.g. `cms-footer`, `cms-breadcrumb-menu`). */
    name: string
    /** Rendered when the named fragment isn't published/compiled yet. Defaults to nothing. */
    fallback?: Children
}

/**
 * Renders a named editable fragment from `cms_fragment_cache`. Used both by layout chrome (imported
 * directly) and by authored pages (registered in the CMS catalog as `<CmsFragment name="…"/>`). The
 * cache read is lazy — render order relative to `load_cms_fragments()` doesn't matter.
 */
export class CmsFragment extends MithrilComponent<CmsFragmentAttrs> {
    view(vnode: m.Vnode<CmsFragmentAttrs>): Children {
        const entry = cms_fragment_cache.get(vnode.attrs.name)
        if (!entry) {
            return vnode.attrs.fallback ?? null
        }
        return m(entry.component)
    }
}
